// ---------------------------------------------------------------------------
// Rank distribution weights and "tier+" weighted stat computation
// ---------------------------------------------------------------------------
//
// Blizzard publishes win/pick rates per rank tier but not player counts,
// so aggregating across multiple tiers (e.g. "Platinum+") requires estimated
// weights. The weights below approximate the known Overwatch competitive
// rank distribution and are used to produce weighted-average hero stats.
//
// Keep this file free of SvelteKit-specific imports ($lib, $app, etc.)
// so it can be consumed by both the frontend and scripts if needed.
// ---------------------------------------------------------------------------

import type { RawHeroStats, HistoryTuple } from "$lib/types";

/** Ordered list of Blizzard competitive rank tiers (lowest → highest). */
export const ORDERED_TIERS = ["Bronze", "Silver", "Gold", "Platinum", "Diamond", "Master", "Grandmaster"] as const;

export type RankTier = (typeof ORDERED_TIERS)[number];

/**
 * Approximate Overwatch competitive rank distribution.
 *
 * These are community-estimated percentages, not official Blizzard figures.
 * They are used as weights when computing cross-rank averages (e.g. "Gold+").
 */
export const RANK_WEIGHTS: Record<RankTier, number> = {
    Bronze: 0.08,
    Silver: 0.18,
    Gold: 0.26,
    Platinum: 0.22,
    Diamond: 0.15,
    Master: 0.07,
    Grandmaster: 0.04,
};

/**
 * Synthetic "+" tier definitions. Each entry is [plusKey, includedTiers[]].
 *
 * "Silver+" means the weighted average of Silver through Grandmaster.
 * Tiers are ordered lowest → highest to match ORDERED_TIERS.
 */
const PLUS_TIER_ENTRIES: [string, RankTier[]][] = [
    ["Bronze+", ["Bronze", "Silver", "Gold", "Platinum", "Diamond", "Master", "Grandmaster"]],
    ["Silver+", ["Silver", "Gold", "Platinum", "Diamond", "Master", "Grandmaster"]],
    ["Gold+", ["Gold", "Platinum", "Diamond", "Master", "Grandmaster"]],
    ["Platinum+", ["Platinum", "Diamond", "Master", "Grandmaster"]],
    ["Diamond+", ["Diamond", "Master", "Grandmaster"]],
    ["Master+", ["Master", "Grandmaster"]],
];

/**
 * Map from plus-tier key → included tier names.
 * e.g. "Gold+" → ["Gold", "Platinum", "Diamond", "Master", "Grandmaster"]
 */
export const PLUS_TIER_INCLUDES = new Map<string, RankTier[]>(PLUS_TIER_ENTRIES);

/** Returns true for synthetic "tier+" keys like "Silver+", "Gold+", etc. */
export function isPlusTier(tierKey: string): boolean {
    return PLUS_TIER_INCLUDES.has(tierKey);
}

/** Returns the base rank name from a plus-tier key (e.g. "Gold+" → "Gold"). */
export function getBaseTier(tierKey: string): string {
    return tierKey.endsWith("+") ? tierKey.slice(0, -1) : tierKey;
}

/** Returns the included tiers for a plus-tier key, or null if not a plus-tier. */
export function getPlusTierIncludes(tierKey: string): RankTier[] | null {
    return PLUS_TIER_INCLUDES.get(tierKey) ?? null;
}

/**
 * Compute weighted-average hero stats across a set of rank tiers.
 *
 * Each tier is weighted by its approximate share of the player population
 * (RANK_WEIGHTS). Heroes missing from any tier are excluded from that tier's
 * contribution (the remaining weight is redistributed proportionally).
 *
 * For ban rate, only tiers that have a non-null banRate are averaged. If
 * none do, the weighted banRate is null (preserving "no data ever recorded"
 * as distinct from a true 0).
 *
 * @param competitiveData - The stats.data.competitive map (tier → map → heroes)
 * @param plusTierKey     - A plus-tier key (e.g. "Gold+")
 * @param map             - Map key to use, falls back to "all"
 */
export function computeWeightedStats(
    competitiveData: Record<string, Record<string, RawHeroStats[]>>,
    plusTierKey: string,
    map = "all",
): RawHeroStats[] {
    const tiers = getPlusTierIncludes(plusTierKey);
    if (!tiers) return [];

    // Collect per-tier arrays (prefer requested map, fall back to "all")
    const tierArrays: [RankTier, RawHeroStats[]][] = [];
    for (const tier of tiers) {
        const tierData = competitiveData[tier];
        if (!tierData) continue;
        const heroes = tierData[map] ?? tierData["all"] ?? [];
        if (heroes.length) tierArrays.push([tier, heroes]);
    }

    if (tierArrays.length === 0) return [];

    // Build a hero-slug set from the first available tier
    const referenceHeroes = tierArrays[0][1];

    return referenceHeroes.flatMap((ref) => {
        let wrSum = 0;
        let prSum = 0;
        let totalWeight = 0;
        let brSum = 0;
        let brWeight = 0;

        for (const [tier, heroes] of tierArrays) {
            const h = heroes.find((x) => x.slug === ref.slug);
            if (!h) continue;
            const w = RANK_WEIGHTS[tier] ?? 0;
            wrSum += h.winRate * w;
            prSum += h.pickRate * w;
            totalWeight += w;
            if (h.banRate !== null && h.banRate !== undefined && h.banRate >= 0) {
                brSum += h.banRate * w;
                brWeight += w;
            }
        }

        if (totalWeight === 0) return [];
        return [
            {
                name: ref.name,
                slug: ref.slug,
                role: ref.role,
                subrole: ref.subrole,
                winRate: wrSum / totalWeight,
                pickRate: prSum / totalWeight,
                banRate: brWeight > 0 ? brSum / brWeight : null,
            } satisfies RawHeroStats,
        ];
    });
}

/**
 * TierInfo-compatible objects for the synthetic "+" tiers.
 * Ordered to match their insertion points in the extended tier list.
 */
export const PLUS_TIER_INFOS: { key: string; name: string }[] = PLUS_TIER_ENTRIES.map(([key]) => ({
    key,
    name: key, // e.g. "Silver+"
}));

/**
 * Compute a weighted [winRate, pickRate, banRate] triple from a per-tier
 * competitive history record for a given plus-tier key.
 *
 * Accepts both legacy 2-tuples (no BR) and new 3-tuples (with BR). BR is
 * averaged only across tiers that have non-null BR; if no tier does, the
 * returned banRate is `null` — preserving "no data" distinctly from true 0.
 *
 * Returns `null` when no tier has data (hero should be skipped).
 */
export function computeWeightedRates(
    competitive: Record<string, HistoryTuple>,
    plusTierKey: string,
): [number, number, number | null] | null {
    if (plusTierKey === "Bronze+") {
        const tuple = competitive["All"];
        if (!tuple) return null;
        return tupleWithBr(tuple);
    }
    const tiers = getPlusTierIncludes(plusTierKey);
    if (!tiers) return null;
    let wrSum = 0;
    let prSum = 0;
    let totalWeight = 0;
    let brSum = 0;
    let brWeight = 0;
    for (const t of tiers) {
        const tuple = competitive[t];
        if (!tuple) continue;
        const w = RANK_WEIGHTS[t] ?? 0;
        wrSum += tuple[0] * w;
        prSum += tuple[1] * w;
        totalWeight += w;
        const br = tuple.length === 3 ? tuple[2] : null;
        if (br !== null && br !== undefined && br >= 0) {
            brSum += br * w;
            brWeight += w;
        }
    }
    if (totalWeight === 0) return null;
    return [wrSum / totalWeight, prSum / totalWeight, brWeight > 0 ? brSum / brWeight : null];
}

/** Convert a HistoryTuple to a uniform 3-element form with `null` BR when absent. */
function tupleWithBr(t: HistoryTuple): [number, number, number | null] {
    return t.length === 3 ? [t[0], t[1], t[2]] : [t[0], t[1], null];
}
